import React, { ReactElement, useEffect, useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { getApiUrl } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { toast } from "react-toastify";
import {
  Pencil,
  Trash,
  Ellipsis,
  Warehouse,
  Building2,
  AlertCircle,
  Settings,
} from "lucide-react";
import { Company, Facility } from "@/types";
import { useTranslation } from "react-i18next";
import { DeleteModal } from "@/components/delete-modal";
import FacilitiesForm from "./FacilitiesForm";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import FacilitiesCategoriesForm from "./FacilitiesCategoriesForm";
import { useNavigate } from "react-router-dom";
import { companyAtom } from "@/store/company";
import { useAtom } from "jotai";
import CompanyInfo from "../companies/CompanyInfo";
import FacilitiesMap from "./FacilitiesMap";
import { VehiclesList } from "../vehicles/VehiclesList";
import { PageHeader } from "@/components/PageHeader";
import ComapaniesIcon from "@/components/icons/CompaniesIcon";

interface ApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Facility[];
}

export default function FacilitiesList(): ReactElement {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCategoriesDrawerOpen, setIsCategoriesDrawerOpen] = useState(false);
  const [editingFacilityId, setEditingFacilityId] = useState<
    number | undefined
  >();
  const [facilityToDelete, setFacilityToDelete] = useState<number | null>(null);
  const [company] = useAtom(companyAtom);

  console.log("company in facilities list", company);

  useEffect(() => {
    if (!company) {
      navigate("/companies");
    }
  }, [company]);

  const { data, isLoading, isError, error } = useQuery<ApiResponse>({
    queryKey: ["facilities", company?.id],
    // enabled: user?.rol === "admin" || !!user?.company?.id,
    queryFn: async () => {
      const urlParams =
        user?.rol === "admin"
          ? company?.id
            ? `?company=${company.id}`
            : ``
          : company?.id
            ? `?company=${company.id}`
            : `?company=${user?.company?.id}`;
      const response = await fetch(getApiUrl(`facilities/${urlParams}`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch facilities");
      }
      return response.json();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (facilityId: number) => {
      const response = await fetch(getApiUrl(`facilities/${facilityId}/`), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to delete company");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["facilities"] });
      toast.success(
        t("facilities.deleteSuccessMessage", "Facility deleted successfully"),
      );
      setFacilityToDelete(null);
    },
    onError: (error: Error) => {
      toast.error(
        error.message ||
          t("facilities.deleteErrorMessage", "Failed to delete facility"),
      );
      setFacilityToDelete(null);
    },
  });

  const handleAdd = () => {
    setEditingFacilityId(undefined);
    setIsDrawerOpen(true);
  };

  const handleEdit = (facilityId: number) => {
    setEditingFacilityId(facilityId);
    setIsDrawerOpen(true);
  };

  const handleDetails = (facilityId: number) => {
    navigate(`/facilities/${facilityId}`);
  };

  const handleDeleteClick = (facilityId: number) => {
    setFacilityToDelete(facilityId);
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["facilities"] });
    setIsDrawerOpen(false);
    setIsCategoriesDrawerOpen(false);
  };

  if (error) {
    return (
      <div className="text-destructive">Error: {(error as Error).message}</div>
    );
  }

  return (
    <div className="pb-8">
      {/* Company Info Card */}
      <CompanyInfo />

      <FacilitiesMap />

      <PageHeader
        icon={
          <ComapaniesIcon
            width={180}
            height={180}
            className="w-[50px] h-[50px]"
          />
        }
        actionTitle="facilities.add"
        title="facilities.title"
        description="facilities.description"
        handleNew={handleAdd}
      />

      <div className="px-4">
        <div className="mt-8">
          {isLoading ? (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <Card key={i} className="overflow-hidden">
                  <div className="p-6">
                    <Skeleton className="h-12 w-12 rounded-full mb-4" />
                    <Skeleton className="h-5 w-[180px] mb-2" />
                    <Skeleton className="h-4 w-[150px] mb-1" />
                    <Skeleton className="h-4 w-[120px] mb-1" />
                    <Skeleton className="h-4 w-[100px]" />
                  </div>
                </Card>
              ))}
            </div>
          ) : isError ? (
            <div className="col-span-full text-center py-8">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>
                  {error instanceof Error
                    ? error.message
                    : "Failed to load facilities"}
                </AlertDescription>
              </Alert>
            </div>
          ) : !data?.results?.length ? (
            <div className="col-span-full text-center py-8">
              <p className="text-muted-foreground">No facilities found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {data.results.map((facility: Facility) => (
                <Card
                  key={facility.id}
                  className="overflow-hidden hover:cursor-pointer hover:bg-muted transform hover:scale-105 transition duration-300"
                  // onClick={() => handleDetails(facility.id)}
                  onClick={() => handleEdit(facility.id)}
                >
                  <CardHeader className="relative pb-0">
                    <div className="absolute right-4 top-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                          >
                            <Ellipsis className="h-4 w-4" />
                            <span className="sr-only">Open menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              handleEdit(facility.id);
                            }}
                          >
                            <Pencil className="h-4 w-4 mr-2" />
                            {t("common.edit")}
                          </DropdownMenuItem>
                          {/* <DropdownMenuItem
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            setEditingFacilityId(facility.id);
                            setIsCategoriesDrawerOpen(true);
                          }}
                        >
                          <Warehouse className="h-4 w-4 mr-2" />
                          {t("common.categories")}
                        </DropdownMenuItem> */}
                          <DropdownMenuItem
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              handleDeleteClick(facility.id);
                            }}
                            className="text-destructive"
                          >
                            <Trash className="h-4 w-4 mr-2" />
                            {t("common.delete")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="h-12 w-12 rounded-full overflow-hidden bg-accent flex items-center justify-center">
                        {facility.logo ? (
                          <img
                            src={facility.logo}
                            alt={facility.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <span className="text-lg font-medium">
                            {facility.name.charAt(0)}
                          </span>
                        )}
                      </div>
                      <div>
                        <h3 className="font-medium">{facility.name}</h3>
                        <Badge
                          variant={facility.is_active ? "default" : "secondary"}
                          className="mt-1"
                        >
                          {facility.is_active
                            ? t("common.active")
                            : t("common.inactive")}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-4">
                    {facility.email && (
                      <p className="text-sm text-muted-foreground mb-1">
                        {facility.email}
                      </p>
                    )}
                    {facility.phone && (
                      <p className="text-sm text-muted-foreground mb-1">
                        {facility.phone}
                      </p>
                    )}
                    {facility.mobile && (
                      <p className="text-sm text-muted-foreground">
                        {facility.mobile}
                      </p>
                    )}
                  </CardContent>
                  <CardFooter className="border-t pt-4">
                    <div className="text-xs text-muted-foreground">
                      {t("facilities.location")}:{" "}
                      {facility.country ? facility.country.name : "-"}
                    </div>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="h-8" />

      <VehiclesList />

      {/* facility edit form */}
      <Drawer
        open={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        direction="right"
      >
        <DrawerContent className="h-full w-[500px] ml-auto">
          <DrawerHeader>
            <DrawerTitle>
              {editingFacilityId ? t("facilities.edit") : t("facilities.add")}
            </DrawerTitle>
            <DrawerDescription>
              {editingFacilityId
                ? t("facilities.edit-description")
                : t("facilities.add-description")}
            </DrawerDescription>
          </DrawerHeader>
          <FacilitiesForm
            facilityId={editingFacilityId}
            onSuccess={handleSuccess}
            onCancel={() => setIsDrawerOpen(false)}
          />
        </DrawerContent>
      </Drawer>

      {/* facility categories form */}
      <Drawer
        open={isCategoriesDrawerOpen}
        onOpenChange={setIsCategoriesDrawerOpen}
        direction="right"
      >
        <DrawerContent className="h-full w-[500px] ml-auto">
          <DrawerHeader>
            <DrawerTitle>
              {editingFacilityId ? t("facilities.edit") : t("facilities.add")}
            </DrawerTitle>
            <DrawerDescription>
              {editingFacilityId
                ? t("facilities.edit-description")
                : t("facilities.add-description")}
            </DrawerDescription>
          </DrawerHeader>
          <FacilitiesCategoriesForm
            facilityId={editingFacilityId}
            onSuccess={handleSuccess}
            onCancel={() => setIsCategoriesDrawerOpen(false)}
          />
        </DrawerContent>
      </Drawer>

      {/* Delete confirmation modal */}
      <DeleteModal
        open={facilityToDelete !== null}
        onOpenChange={(open) => !open && setFacilityToDelete(null)}
        entity={t("facilities.title", "facility").toLowerCase()}
        onDelete={() => deleteMutation.mutate(facilityToDelete!)}
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}
